/**
 * The day ↔ psalm join — deliberately DARK, and this file is where that is decided.
 *
 * ## Why this renders nothing today
 *
 * "Psalm 25, appointed for Advent 1 in Year C" is the pairing worth publishing; a
 * psalm on its own competes with every Bible site on the internet. But the table of
 * which reading falls on which day is **© 1992 Consultation on Common Texts**,
 * administered by Augsburg Fortress, and `data/lectionary/PROVENANCE.md` is explicit:
 *
 * > CCT allows free reproduction by non-profit, non-commercial groups and by member
 * > denominations' publishers; Fugue Lab is neither, so a commercial or lead-gen
 * > surface needs written permission.
 *
 * `fuguelab.com` is a lead-gen surface. We do not have permission. So the join is
 * built, tested and wired all the way to the components — and fed
 * `RCL_PERMISSION_NONE`, which makes `extractPsalmAppointments` return `[]`. Every
 * consumer therefore renders nothing, with no per-component gate to forget.
 *
 * ## Turning it on
 *
 * One edit, here, and nowhere else:
 *
 * ```ts
 * const PERMISSION: RclPermission = {
 *   grantedBy: 'Augsburg Fortress (Consultation on Common Texts)',
 *   grantedAt: '2026-__-__',
 *   scope: 'reproduction of RCL citations on fuguelab.com/hymnal',
 * }
 * ```
 *
 * Then `attributionLine()` MUST render wherever the data appears — the components
 * already do this whenever `hasAppointments` is true, so it comes along for free.
 * Do not replace the gate with a boolean; the value exists so the grant is written
 * down in a diff rather than being a `true` nobody can trace to a decision.
 *
 * ## Why the corpus is imported at all when it yields nothing
 *
 * So the wiring is exercised. A join that is only written on the day permission
 * arrives is a join nobody has ever run; this one is type-checked against the real
 * dataset and covered by `psalm-appointments.test.ts` at 231 appointments across all
 * three cycles. Turning it on is a config change, not a build.
 *
 * ## The import costs nothing, and that is MEASURED, not assumed
 *
 * `extractPsalmAppointments` returns `[]` before touching the data, so Rollup drops
 * the JSON. That matters for more than bundle size: if the file were bundled we
 * would be **shipping** the CCT table to every visitor while merely declining to
 * render it, which is distribution either way.
 *
 * So it was checked against the built artifact rather than reasoned about — 33
 * citations exist in `revised-common-lectionary.json` and in no other dataset this
 * site bundles (the propers data also cites psalms, which is why a naive grep for
 * `Psalm \d+:\d+` finds 201 hits and proves nothing). Re-run after any change here:
 *
 * ```bash
 * npm run build
 * node -e "const d=require('@gjl/music-manager/data/lectionary/revised-common-lectionary.json'),
 *   gp=require('fs').readFileSync(require.resolve('@gjl/music-manager/data/propers/gradual-psalms.json'),'utf8');
 *   console.log(d.map(e=>e.readings?.psalm?.citation).filter(c=>c?.includes(', ')&&!gp.includes(c)).join('\n'))" \
 *   > /tmp/rcl-uniq.txt
 * while read -r c; do grep -qF "$c" dist/assets/*.js && echo "LEAKED: $c"; done < /tmp/rcl-uniq.txt
 * ```
 *
 * Last run 2026-08-11: 0 of 33 present. The unit-level half of this — that the
 * indexes are empty — is `src/lib/psalter.test.tsx`.
 */
import rcl from '@gjl/music-manager/data/lectionary/revised-common-lectionary.json'
import {
  extractPsalmAppointments,
  byPsalm,
  byDay,
  attributionLine,
  RCL_PERMISSION_NONE,
  type RawLectionaryDay,
} from '@gjl/music-manager/psalm-appointments'

/**
 * ⛔ THE GATE. `RCL_PERMISSION_NONE` is the current, deliberate state — not a TODO
 * anyone forgot. Read this file's header before changing it.
 */
const PERMISSION = RCL_PERMISSION_NONE

const appointments = extractPsalmAppointments(rcl as RawLectionaryDay[], PERMISSION)

/** psalm number → the days it is appointed for. Empty while the gate is closed. */
export const appointmentsByPsalm = byPsalm(appointments)

/** day id → what is appointed there across cycles. Empty while the gate is closed. */
export const appointmentsByDay = byDay(appointments)

/**
 * Whether ANY appointment data is publishable right now. Components branch on this
 * rather than on `.length`, so the reason for the empty state stays greppable.
 */
export const hasAppointments = appointments.length > 0

/** The notice CCT requires. Render it wherever appointment data is shown. */
export const rclAttribution = attributionLine()
